import type {
  ExpenseClass,
  ExpenseEnumsResponse,
  ExpenseLineItem,
  InterestContext,
  TimingAnchor,
} from '../types';

// ─────────────────────────────────────
// Subtype unit hints (display-only)
// ─────────────────────────────────────
//
// Unit labels shown alongside certain subtypes. These are presentation hints,
// not part of the BE enum contract, so they stay local. Subtype labels are
// identical on the FE and the BE enums endpoint, so keying by label is stable.

/** Unit labels shown alongside certain OpEx subtypes. */
export const OPEX_SUBTYPE_UNITS: Record<string, string> = {
  'Oil Operating Expense': '$/BBL',
  'Gas Operating Expense': '$/MCF',
  'Water Operating Expense': '$/BBL water',
};

/** Unit labels shown alongside certain CapEx subtypes. */
export const CAPEX_SUBTYPE_UNITS: Record<string, string> = {
  'Lease Bonus': '$/acre',
};

// Returns the unit label for a subtype, if any.
export const subtypeUnit = (subtype: string): string | undefined =>
  OPEX_SUBTYPE_UNITS[subtype] ?? CAPEX_SUBTYPE_UNITS[subtype];

// ─────────────────────────────────────
// Subtypes (sourced from GET /expense_scenarios/enums)
// ─────────────────────────────────────

/**
 * Returns the available subtypes for a given expense class + interest context,
 * from the BE enums payload. Defaults to the WI Owner list when no context is
 * selected. Returns an empty list until the enums have loaded.
 * @param {ExpenseEnumsResponse | null} enums - The loaded enums payload
 * @param {ExpenseClass} cls - The expense class (opex/capex)
 * @param {InterestContext} [context] - The interest context
 * @return {string[]} The list of subtypes
 */
export function getSubtypes(
  enums: ExpenseEnumsResponse | null,
  cls: ExpenseClass,
  context?: InterestContext
): string[] {
  if (!enums) return [];
  const byContext = enums.subtypes[cls];
  return context === 'royalty_owner'
    ? byContext.royaltyOwner
    : byContext.wiOwner;
}

// ─────────────────────────────────────
// Buyer Commission
// ─────────────────────────────────────

/** Subtype whose amount cell is replaced by the Buyer Commission editor. */
export const BUYER_COMMISSION_SUBTYPE = 'Buyer Commission';

/**
 * Returns true if the item uses the Buyer Commission (At Target / At Max) editor.
 *
 * Normalises the way both other systems do before comparing — Rails with
 * `subtype.to_s.strip.casecmp?` and eval_calculator with
 * `subtype.strip().lower()`. `subtype` is a free varchar the API accepts
 * verbatim, so padding and odd casing are reachable from any client. Matching
 * exactly here would disagree with the server about what a row is: the basis
 * gate below would demand a "Percentage of" selection on a row Rails treats as
 * exempt, disabling Save for the whole scenario with no way to satisfy it.
 * @param {ExpenseLineItem} item - The line item
 * @return {boolean} True when the item is a Buyer Commission
 */
export const isBuyerCommission = (item: ExpenseLineItem): boolean =>
  String(item.subtype ?? '')
    .trim()
    .toLowerCase() === BUYER_COMMISSION_SUBTYPE.toLowerCase();

// ─────────────────────────────────────
// Percentage basis
// ─────────────────────────────────────

/**
 * Whether this item should show the "Percentage of" basis selection. Percent
 * items need a declared basis; Buyer Commission does not, because it is always
 * a percentage of total package value.
 * @param {ExpenseLineItem} item - The line item
 * @return {boolean} True when the basis selection applies
 */
export const needsPercentBasis = (item: ExpenseLineItem): boolean =>
  item.amountType === 'percent' && !isBuyerCommission(item);

/**
 * Whether the item's basis requirement is satisfied. Gates the Save button
 * alongside the inline hint.
 * @param {ExpenseLineItem} item - The line item
 * @return {boolean} True when no basis is needed, or one is selected
 */
export const hasRequiredPercentBasis = (item: ExpenseLineItem): boolean =>
  !needsPercentBasis(item) || Boolean(item.percentBasis);

// ─────────────────────────────────────
// Percent value conversion
// ─────────────────────────────────────
//
// The API stores percentages fractionally — 2.5% is 0.025 — so the arithmetic
// downstream is a plain multiply. The editor shows the number the user thinks
// in, so every percent value is divided by 100 on the way out and multiplied
// by 100 on the way back in. Fixed dollar amounts pass through untouched.

const PERCENT_SCALE = 100;

/**
 * Divides by 100, rounding away the binary float dust that produces values
 * like 0.024999999999999998 (2.5 becomes 0.025).
 * @param {number} n - The displayed percentage
 * @return {number} The fractional equivalent
 */
const roundFraction = (n: number): number =>
  Number((n / PERCENT_SCALE).toFixed(10));

/**
 * Converts a displayed percentage to its fractional wire value.
 * @param {number | null | undefined} value - The displayed value (2.5 for 2.5%)
 * @param {boolean} isPercent - Whether the amount is a percentage
 * @return {number | null} The wire value, or null when there is nothing to send
 */
export function toWireAmount(
  value: number | null | undefined,
  isPercent: boolean
): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  return isPercent ? roundFraction(value) : value;
}

/**
 * Converts a fractional wire value back to the displayed percentage.
 * @param {number | null | undefined} value - The wire value (0.025 for 2.5%)
 * @param {boolean} isPercent - Whether the amount is a percentage
 * @return {number | null} The value to display
 */
export function fromWireAmount(
  value: number | null | undefined,
  isPercent: boolean
): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  return isPercent ? Number((value * PERCENT_SCALE).toFixed(10)) : value;
}

// ─────────────────────────────────────
// LOE schedule helpers
// ─────────────────────────────────────

/**
 * Cadence shown in the Timing cell for an LOE year row.
 *
 * MONTHLY, and pinned so it cannot drift back. "Year N" selects WHICH monthly
 * rate applies during year N of production — it does not make the amount
 * annual. eval_calculator charges the year's schedule entry on every monthly
 * row with no division by 12 (append_costs.py#apply_loe_fixed_well_costs), and
 * the eval-2 Scenario form labels the same underlying field "$/month".
 *
 * This read "Annual" until Sep 2026, which invited LOE amounts entered 12x too
 * small — or correct amounts read as 12x too high.
 */
export const LOE_TIMING_LABEL = 'Monthly';

// Returns true if the item is part of the LOE year schedule.
export const isLoeItem = (item: ExpenseLineItem): boolean =>
  item.expenseClass === 'opex' && item.subtype.startsWith('LOE — Year');

// Parses the year number out of an LOE subtype string.
export const getLoeYear = (subtype: string): number => {
  const m = subtype.match(/Year (\d+)/);
  return m ? parseInt(m[1], 10) : 0;
};

// ─────────────────────────────────────
// Timing defaults
// ─────────────────────────────────────

/**
 * The timing a line item takes when it is created, by expense class.
 *
 * These are REAL values, not placeholders. is_recurring false means
 * 'one-time, active only in the start month' to the calculator
 * (eval_calculator prepare/timing.py), so an item whose timing has never
 * been touched is already charged that way. Defaulting by class is what
 * makes the common case correct without interaction: nearly all OpEx is
 * monthly for the life of the well, and nearly all CapEx is a single charge
 * on one date.
 *
 * Only defaultTiming reads this, and everything that creates or
 * re-characterises a row goes through defaultTiming, so no caller can be left
 * on a stale cadence — or take one without the matching reference date.
 */
export const TIMING_DEFAULTS: Record<
  ExpenseClass,
  Pick<
    ExpenseLineItem,
    | 'offsetMonths'
    | 'isRecurring'
    | 'recurringIntervalMonths'
    | 'recurringOccurrences'
  >
> = {
  opex: {
    offsetMonths: 0,
    isRecurring: true,
    recurringIntervalMonths: 1,
    recurringOccurrences: 'continuous',
  },
  capex: {
    offsetMonths: 0,
    isRecurring: false,
    recurringIntervalMonths: undefined,
    recurringOccurrences: undefined,
  },
};

// ─────────────────────────────────────
// Timing anchor (the reference date)
// ─────────────────────────────────────

/**
 * What a row with NO stored anchor is actually doing.
 *
 * eval_calculator resolves a missing anchor to the evaluation effective date
 * (prepare/timing.py#resolve_anchor_date), so that — not the class default
 * below — is the truth about every row saved before this field existed.
 * Rendering the class default for those rows would tell the user a monthly
 * OpEx counts from first production when it does not.
 *
 * The class defaults apply to row CREATION only. They are deliberately not a
 * read-time fallback: adopting them on load would silently re-time every
 * existing line item on the next save.
 */
export const IMPLICIT_TIMING_ANCHOR: TimingAnchor = 'effective_date';

/**
 * Compact anchor wording for the collapsed Timing cell, which is one narrow
 * grid column. The timing editor uses the full BE labels instead.
 */
const ANCHOR_SHORT_LABELS: Record<TimingAnchor, string> = {
  first_production: 'first prod',
  effective_date: 'eff date',
  absolute: 'fixed date',
  economic_limit: 'econ limit',
};

/**
 * The anchor this item behaves as, stored or implied. Never null — an item
 * always counts from something.
 * @param {ExpenseLineItem} item - The line item
 * @return {TimingAnchor} The effective anchor
 */
export const resolveTimingAnchor = (item: ExpenseLineItem): TimingAnchor =>
  item.timingAnchor ?? IMPLICIT_TIMING_ANCHOR;

/**
 * The anchor a NEW line item of this subtype/class should carry. Mirrors
 * ExpenseScenario::LineItem.default_timing_anchor, but reads the maps off the
 * enums payload rather than restating them, so the two cannot drift.
 * @param {ExpenseEnumsResponse} enums - The loaded enums payload
 * @param {string} subtype - The line item subtype
 * @param {ExpenseClass} expenseClass - The expense class
 * @return {TimingAnchor} The anchor to assign
 */
export function defaultTimingAnchor(
  enums: ExpenseEnumsResponse,
  subtype: string,
  expenseClass: ExpenseClass
): TimingAnchor {
  const { bySubtype, byClass } = enums.timingAnchorDefaults;
  return bySubtype[subtype] ?? byClass[expenseClass] ?? IMPLICIT_TIMING_ANCHOR;
}

/**
 * Everything a line item's timing consists of. What a row is created with, and
 * what a class change re-derives.
 */
export type DefaultTiming = Pick<
  ExpenseLineItem,
  | 'offsetMonths'
  | 'isRecurring'
  | 'recurringIntervalMonths'
  | 'recurringOccurrences'
  | 'timingAnchor'
>;

/** An anchor, plus whatever cadence that anchor forces. */
export type TimingAnchorPatch = Pick<
  ExpenseLineItem,
  | 'timingAnchor'
  | 'isRecurring'
  | 'recurringIntervalMonths'
  | 'recurringOccurrences'
>;

/**
 * True for an expense that happens at the end of the well's life.
 * @param {TimingAnchor | undefined} anchor - The anchor to test
 * @return {boolean} Whether the anchor is the end of economic life
 */
export const isTerminalAnchor = (anchor: TimingAnchor | undefined): boolean =>
  anchor === 'economic_limit';

/**
 * The patch that setting a reference date makes — cadence included.
 *
 * An expense anchored to the end of economic life happens ONCE: there is no
 * "every month after the well dies", and TO LIFE has no recurring form in a
 * deck. So the anchor does not merely travel alongside a cadence, it
 * constrains one, which is why the rule lives here instead of at each call
 * site. Every path that can change an anchor goes through it: row creation and
 * a class change via defaultTiming, and the Reference-date dropdown in the
 * timing editor.
 *
 * Without it the dropdown rebuilds by hand the combination defaultTiming
 * exists to prevent — a plugging cost charged every month forever, starting at
 * the economic limit.
 * @param {TimingAnchor | undefined} timingAnchor - The anchor being set
 * @return {TimingAnchorPatch} The anchor and any cadence it forces
 */
export function timingAnchorPatch(
  timingAnchor: TimingAnchor | undefined
): TimingAnchorPatch {
  if (!isTerminalAnchor(timingAnchor)) return { timingAnchor };

  return {
    timingAnchor,
    isRecurring: false,
    recurringIntervalMonths: undefined,
    recurringOccurrences: undefined,
  };
}

/**
 * The timing a row takes on creation, or when a class change re-characterises
 * it: the class cadence from TIMING_DEFAULTS plus the subtype's reference date.
 *
 * The two defaults need one correction where they meet. TIMING_DEFAULTS is
 * per class, so an OpEx subtype whose reference date is the end of economic
 * life — Abandonment — would inherit monthly-continuous and charge a plugging
 * cost every month forever, starting at the economic limit. That correction is
 * timingAnchorPatch, shared with the timing editor so the two cannot diverge.
 * @param {ExpenseEnumsResponse} enums - The loaded enums payload
 * @param {string} subtype - The line item subtype
 * @param {ExpenseClass} expenseClass - The expense class
 * @return {DefaultTiming} The timing to assign
 */
export function defaultTiming(
  enums: ExpenseEnumsResponse,
  subtype: string,
  expenseClass: ExpenseClass
): DefaultTiming {
  const timingAnchor = defaultTimingAnchor(enums, subtype, expenseClass);
  return {
    ...TIMING_DEFAULTS[expenseClass],
    ...timingAnchorPatch(timingAnchor),
  };
}

/**
 * What a subtype swap does to an EXISTING row's timing. Three cases, narrowing:
 *
 * Crossing into or out of an end-of-life expense re-derives everything. That
 * changes what the row is, and the cadence has to follow the anchor — a swap
 * to Abandonment must not leave a monthly charge behind.
 *
 * Short of that boundary, a row that stored an anchor re-derives the reference
 * date only, so a hand-tuned interval survives an ordinary subtype swap.
 *
 * A row that never stored an anchor keeps its null. Those rows run on the
 * evaluation effective date, and stamping a subtype default on one would
 * silently re-time timing the user never opened: the offset still reads "10
 * months", but ten months after first production rather than after the
 * effective date. Null is the same honest value on this edit that it is on
 * read — see IMPLICIT_TIMING_ANCHOR.
 * @param {ExpenseEnumsResponse} enums - The loaded enums payload
 * @param {ExpenseLineItem} item - The row being edited
 * @param {string} subtype - The subtype being swapped in
 * @return {Partial<ExpenseLineItem>} The timing patch to apply
 */
export function subtypeTimingPatch(
  enums: ExpenseEnumsResponse,
  item: ExpenseLineItem,
  subtype: string
): Partial<ExpenseLineItem> {
  const timing = defaultTiming(enums, subtype, item.expenseClass);
  const wasTerminal = isTerminalAnchor(resolveTimingAnchor(item));
  const isTerminal = isTerminalAnchor(timing.timingAnchor);

  if (wasTerminal !== isTerminal) return timing;
  if (item.timingAnchor === undefined) return {};
  return { timingAnchor: timing.timingAnchor };
}

// ─────────────────────────────────────
// Standard OpEx quick-add preset
// ─────────────────────────────────────

const opexPreset = (subtype: string): Omit<ExpenseLineItem, 'id'> => ({
  expenseClass: 'opex',
  subtype,
  context: 'wi_owner',
  description: '',
  amountType: 'fixed',
  amount: null,
  ...TIMING_DEFAULTS.opex,
});

/** Standard OpEx preset — LOE Years 1-5 plus variable commodity rates. */
export const STANDARD_OPEX_PRESET: Omit<ExpenseLineItem, 'id'>[] = [
  opexPreset('LOE — Year 1'),
  opexPreset('LOE — Year 2'),
  opexPreset('LOE — Year 3'),
  opexPreset('LOE — Year 4'),
  opexPreset('LOE — Year 5'),
  opexPreset('Oil Operating Expense'),
  opexPreset('Gas Operating Expense'),
  opexPreset('Water Operating Expense'),
];

/**
 * Filters a preset down to the items that should be added given the current
 * list. LOE items are treated as a block — if any LOE item already exists, no
 * LOE preset items are added. Non-LOE items are deduped by subtype.
 * @param {Omit<ExpenseLineItem, 'id'>[]} preset - The preset to filter
 * @param {ExpenseLineItem[]} existing - The current line items
 * @return {Omit<ExpenseLineItem, 'id'>[]} The items to add
 */
export function filterPresetToAdd(
  preset: Omit<ExpenseLineItem, 'id'>[],
  existing: ExpenseLineItem[]
): Omit<ExpenseLineItem, 'id'>[] {
  const hasLoe = existing.some(isLoeItem);
  const existingSubtypes = new Set(existing.map((i) => i.subtype));
  return preset.filter((item) => {
    if (item.subtype.startsWith('LOE — Year')) return !hasLoe;
    return !existingSubtypes.has(item.subtype);
  });
}

// ─────────────────────────────────────
// Timing
// ─────────────────────────────────────

/**
 * The cadence half of the timing label: how often the charge repeats, and how
 * many times.
 *
 * TOTAL BY DESIGN - it never returns null. An item always HAS an effective
 * timing: the calculator reads is_recurring false as 'one-time, active only
 * in the start month' (eval_calculator prepare/timing.py), so there is no
 * such thing as an item whose timing is undecided. An earlier version
 * returned null when offsetMonths was 0 and isRecurring false, which let the
 * row render 'Add Timing' over an item that was already being charged once
 * in month 0 - an unset-looking label on a real and, for OpEx, usually wrong
 * value. Describe what will happen instead.
 * @param {ExpenseLineItem} item - The line item
 * @return {string} The cadence label
 */
export function formatItemCadence(item: ExpenseLineItem): string {
  if (!item.isRecurring) return 'one-time';

  const n = item.recurringIntervalMonths ?? 1;
  const every =
    n === 1
      ? 'monthly'
      : n === 3
        ? 'quarterly'
        : n === 6
          ? 'semi-annual'
          : n === 12
            ? 'annual'
            : `every ${n} mo`;

  const occ = item.recurringOccurrences ?? 'continuous';
  return `${every} · ${occ === 'continuous' ? 'continuous' : `×${occ}`}`;
}

/**
 * The reference-date half: what the charge is measured from, and how far after
 * it. Always present. An offset is meaningless without the date it counts
 * from, and an offset of 0 still says WHEN the charge lands, so there is no
 * case where leaving the reference date out is more readable.
 * @param {ExpenseLineItem} item - The line item
 * @return {string} The reference-date label
 */
export function formatItemReference(item: ExpenseLineItem): string {
  const offset = item.offsetMonths ?? 0;
  const anchor = ANCHOR_SHORT_LABELS[resolveTimingAnchor(item)];
  return offset > 0 ? `${anchor} +${offset}mo` : `from ${anchor}`;
}

/**
 * Both halves on one line. The Timing cell stacks them instead — one line
 * holding both needs an 18rem column at the longest, which is more than the
 * row can spare — so this is the tooltip and the single-string form.
 * @param {ExpenseLineItem} item - The line item
 * @return {string} The compact timing label
 */
export function formatItemTiming(item: ExpenseLineItem): string {
  return `${formatItemCadence(item)} · ${formatItemReference(item)}`;
}
